import config from 'config/app-config';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { useEffect, useState } from 'react';

export enum FeatureFlags {
  AdultMembersEnabled = 'eighteen-to-twenty-five',
  TransperfectLanguageSelectorEnabled = 'transperfect',
  TreatmentPlanForCoreService = 'treatment-plan-for-core-service',
  AspLeadIntakeEnabled = 'lead-intake-enabled',
  ForceRenewalAgreement = 'force-agreement-renewal',
  CollectHospitalizationInfo = 'collect-hospitalization-info',
  DisplayInformedConsents = 'display-consents',
  DisplayMemberFinancialPolicy = 'display-member-financial-policy',
  DisplayUpdatedActivationPage = 'display-updated-activation-page',
  NavigateEligibilityCheck = 'navigate-eligibility-check',
  BackendUserExperience = 'backend-user-experience',
  NavigateStripeSuccess = 'navigate-stripe-success',
  BackendNextStepCards = 'backend-next-step-cards',
  NavigateCoreLanding = 'navigate-core-landing',
  NavigateNpLanding = 'navigate-np-landing',
  ShowGetStartedNp = 'show-get-started-np',
  NpShowNewInsuranceForm = 'np-new-insurance-form',
  ShowGetStartedCore = 'show-get-started-cc',
}

export const LaunchDarklyService = (() => {
  const subscriptions = Object.values(FeatureFlags).reduce(
    (sum, key) => {
      sum[key as FeatureFlags] = [];
      return sum;
    },
    {} as Record<FeatureFlags, ((value: boolean) => void)[]>,
  );

  const emit = (flags: LDClient.LDFlagSet) => {
    for (const key in flags) {
      if (subscriptions[key as FeatureFlags]) {
        subscriptions[key as FeatureFlags].forEach((cb) => cb(flags[key]));
      }
    }
  };

  // singleton, private reference to client (used to prevent multiple initializations)
  let client: LDClient.LDClient;

  const service = {
    // left unassigned until after the client is remotely connected to LD and ready to go
    client: undefined as unknown as LDClient.LDClient,
    initialized: false,

    initialize: async (userId: string) => {
      if (client || config.launchDarklyDisable) {
        return;
      }
      try {
        let logger = {
          debug: console.debug.bind(console),
          info: console.info.bind(console),
          warn: console.warn.bind(console),
          error: console.error.bind(console),
        };

        // Suppress logging in development
        if (process.env['NODE_ENV'] === 'development') {
          logger = {
            debug: () => {},
            info: () => {},
            warn: console.warn.bind(console),
            error: console.error.bind(console),
          };
        }

        client = LDClient.initialize(
          config.launchDarklyKey,
          {
            kind: 'user',
            key: userId,
          },
          {
            logger,
          },
        );

        await client?.waitUntilReady();
        service.initialized = true;

        emit(client?.allFlags());
      } catch (e) {
        console.error(e);
      }

      service.client = client;

      client?.on('change', (changes: LDClient.LDFlagChangeset) => {
        const accumulated: Partial<typeof subscriptions> = {};

        for (const k in changes) {
          if (k in subscriptions) {
            accumulated[k as FeatureFlags] = changes[k].current;
          }
        }

        emit(accumulated);
      });
    },

    getFlag: (flag: FeatureFlags, defaultValue: boolean) => {
      return service.client?.variation(flag, defaultValue) ?? defaultValue;
    },

    subscribe: (flag: FeatureFlags, callback: (value: boolean) => void) => {
      subscriptions[flag]?.push(callback);

      return () => {
        subscriptions[flag]?.splice(subscriptions[flag].indexOf(callback), 1);
      };
    },
  };

  return service;
})();

export const useFlag = (flag: FeatureFlags, defaultValue = false) => {
  const [flagValue, setFlagValue] = useState(LaunchDarklyService.getFlag(flag, defaultValue));

  useEffect(() => {
    const unsubscribe = LaunchDarklyService.subscribe(flag, (value) => setFlagValue(value));

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return flagValue;
};
